import { useCallback, useEffect, useMemo, useState } from "react";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import { useSpecializedTranslation } from "../../hooks/specializedTranslationHook";
import { checkEnabled, checkEnabledValue } from "../../utils/enableUtilities";
import { useSites } from "../../reducers/siteSlice";
import { useLevels } from "../../reducers/levelSlice";
import { useTypes } from "../../reducers/typeSlice";
import Select from "../../common/components/Select";
import { useTranslation } from "react-i18next";
import { chunkArrayIntoColumns, distinct, ensureArray } from "../../common/utils/arrayUtilities";
import { Row } from "../../common/components/Grid";
import { Col } from "react-bootstrap";
import TextInput from "../../common/components/TextInput";
import Checkbox from "../../common/components/Checkbox";
import DefaultButton from "../../common/components/DefaultButton";

export const AssistanceSiteDropdown = ({ site, level, type, job, assistance, onChange, required }) => {
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const sites = useSites();
    const levels = useLevels();
    const types = useTypes();

    const siteRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["site", "level"], job), [clientConfig, job]);
    const expandedTypes = useMemo(() => expandTypes(clientConfig, sites, levels, types), [clientConfig, sites, levels, types]);
    
    const displaySites = useMemo(() => {
        if (!clientConfig.enableSites) {
            return [];
        }
        const enabledSites = sites.filter((s) => 
            checkEnabled(clientConfig, clientConfig.enableAssistance, { site: s?.name }) && (!siteRestricted || s.name === job?.site || assistance?.some((a) => s.name === a.site))
            && (!level || levels.some(l => l.name === level && (!l.site || l.site === site)))
            && (!type || type.length === 0 || types.some(t => ensureArray(type).some(type => t.type === type) && (!t.site || !site || t.site === site) && (!level || !t.level || t.level === level)))
        );

        return distinct(enabledSites.filter((s) => !siteRestricted && credentials.access.hasFull(expandedTypes.filter((t) => t.site === s.name))), "name").concat([{ name: '' }]);
    }, [clientConfig, sites, siteRestricted, job?.site, assistance, level, levels, type, types, site, credentials.access, expandedTypes]);

    useEffect(() => {
        if (displaySites.every(s => s.name !== site)) {
            onChange({ site: displaySites?.length > 0 ? displaySites[0].name : null });
        }
    }, [site, displaySites, onChange])

    return clientConfig.enableSites && displaySites?.length !== 1 && <Select placeholder={t("site", { count: 1 })} value={site} onChange={site => onChange({ site })} options={displaySites} labelField="name" valueField="name" required={required} />
}

export const AssistanceLevelDropdown = ({ site, level, job, assistance, onChange, required }) => {
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const sites = useSites();
    const levels = useLevels();
    const types = useTypes();

    const expandedLevels = useMemo(() => {
        return levels.flatMap(l => l.site ? [l] : sites.map(s => ({ ...l, site: s.name })));
    }, [sites, levels]);

    const levelRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["level"], job), [clientConfig, job]);
    const expandedTypes = useMemo(() => expandTypes(clientConfig, sites, levels, types), [clientConfig, sites, levels, types]);

    const displayLevels = useMemo(() => {
        if (!clientConfig.enableLevels) {
            return [];
        }
        const enabledLevels = expandedLevels.filter((l) => 
            checkEnabled(clientConfig, clientConfig.enableAssistance, { site, level: l.name }) &&
            (!levelRestricted || ((!clientConfig.enableSites || l.site === site) && l.name === level) || assistance?.some((a) => (!clientConfig.enableSites || a.site === site) && l.name === a.level))
        );

        return distinct(enabledLevels.filter((l) => 
            !levelRestricted && credentials.access.hasFull(expandedTypes.filter((t) => t.site === site && t.level === l.name))), "name").concat([{ name: '' }]);
    }, [clientConfig, expandedLevels, site, levelRestricted, level, assistance, credentials.access, expandedTypes]);

    useEffect(() => {
        if (displayLevels.every(l => l.name !== level)) {
            onChange({ level: displayLevels.length > 0 ? displayLevels[0].name : null });
        }
    }, [displayLevels, level, onChange]);

    return clientConfig.enableLevels && displayLevels?.length !== 1 && 
        <Select placeholder={t("level", { count: 1 })} value={level} onChange={level => onChange({ level })} options={displayLevels} labelField="name" valueField="name" required={required} />
}

const expandTypes = (clientConfig, sites, levels, types) => {
    return types.flatMap((t) => {
        let result = [t];
        if (clientConfig.enableLevels) {
            const allowedLevels = levels.filter(l => !clientConfig.enableSites || !t.site || !l.site || l.site === t.site);
            result = result.flatMap((t) => t.level ? [t] : allowedLevels.map((l) => ({ ...t, level: l.name, site: l.site })));
        }
        if (clientConfig.enableSites) {
            result = result.flatMap((t) => (t.site ? [t] : sites.map((s) => ({ ...t, site: s.name }))));
        }
        return result;
    })
}

const buildDisplayTypes = (credentials, clientConfig, types, site, level, type, siteRestricted, levelRestricted, assistance) => {
    const typesToCheck = types.filter((t) =>
        (t.permitAssistance ?? true) &&
        (!clientConfig.enableSites || !siteRestricted || !site || t.site === site) &&
        (!clientConfig.enableLevels || !levelRestricted || !level || t.level === level)
    );

    if (!credentials.access.hasFull(typesToCheck)) {
        return [];
    }

    return distinct(types.filter((t) =>
        (t.permitAssistance ?? true) && (!clientConfig.enableSites || !site || t.site === site) && (!clientConfig.enableLevels || !level || t.level === level) && t.type !== type &&
        !assistance?.some((a) => (!clientConfig.enableSites || a.site === site) && (!clientConfig.enableLevels || a.level === level) && a.type === t.type)
    ), "type");
}


export const AssistanceTypeDropdown = ({ site, level, type, job, assistance, onChange, required }) => {
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const sites = useSites();
    const levels = useLevels();
    const types = useTypes();

    const siteRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["site", "level"], job), [clientConfig, job]);
    const levelRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["level"], job), [clientConfig, job]);
    
    const expandedTypes = useMemo(() => expandTypes(clientConfig, sites, levels, types), [clientConfig, sites, levels, types]);

    const displayTypes = useMemo(() => buildDisplayTypes(credentials, clientConfig, expandedTypes, site, level, job?.type, siteRestricted, levelRestricted, assistance)
        , [expandedTypes, credentials, clientConfig, siteRestricted, site, levelRestricted, level, job?.type, assistance]);

    useEffect(() => {
        if (displayTypes.every(t => t.type !== type)) {
            onChange({ type: displayTypes.length > 0 ? displayTypes[0].type : null });
        }
    }, [displayTypes, onChange, type])

    const handleChange = useCallback(type => {
        const selected = displayTypes.filter(t => t.type === type);
        const delta = { type };
        if (selected.length === 1) {
            if (clientConfig.enableSites && t.site !== site) {
                delta.site = selected.site;
            }
            if (clientConfig.enableLevels && t.level !== level) {
                delta.level = selected.level;
            }
        }
        onChange(delta);
    }, [displayTypes, onChange, displayTypes, clientConfig.enableSites, clientConfig.enableLevels])

    return <Select placeholder={t("type", { count: 1 })} value={type} onChange={handleChange} options={displayTypes} labelField="type" valueField="type" required={required} />;
}

export const AssistanceTypeGrid = ({ site, level, type, job, assistance, onChange }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const sites = useSites();
    const levels = useLevels();
    const types = useTypes();

    const [typeQuery, setTypeQuery] = useState();

    const siteRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["site", "level"], job), [clientConfig, job]);
    const levelRestricted = useMemo(() => checkEnabledValue(clientConfig, clientConfig.assistanceRestriction, ["level"], job), [clientConfig, job]);

    const expandedTypes = useMemo(() => expandTypes(clientConfig, sites, levels, types)
        , [clientConfig, sites, levels, types]);

    const displayTypes = useMemo(() => buildDisplayTypes(credentials, clientConfig, expandedTypes, site, level, job?.type, siteRestricted, levelRestricted, assistance)
        , [expandedTypes, credentials, clientConfig, siteRestricted, site, levelRestricted, level, job?.type, assistance]);

    const filteredTypes = useMemo(() => {
        const query = typeQuery?.trim().toLowerCase();
        if (!query) {
            return displayTypes;
        }
        return displayTypes.filter((t) => t.type?.toLowerCase().includes(query));
    }, [displayTypes, typeQuery]);

    useEffect(() => {
        if (type?.some((st) => displayTypes.every((t) => t.type !== st))) {
            onChange({ type: type.filter((st) => displayTypes.some((t) => t.type === st)) });
        }
    }, [type, onChange, displayTypes]);

    const typeRows = useMemo(() => chunkArrayIntoColumns(filteredTypes), [filteredTypes]);

    return <>
        <Row>
            <Col>
                <TextInput value={typeQuery} live={true} hideLabel prependCtrl={<DefaultButton kind="filter" disabled hideLabel />} placeholder={t('filter', { count: 1 })} onChange={setTypeQuery} />
            </Col>
        </Row>
        {typeRows.map(row => <Row key={row.reduce((str, type) => `${str}_${type.site}_${type.level}_${type.type}`, '')} className="py-1">
            {row.map(t => t ? <Col key={`${t.site}_${t.level}_${t.type}`} xs={3}><Checkbox placeholder={t.type + (((clientConfig.enableSites && !siteRestricted && !site) || (clientConfig.enableLevels && !levelRestricted && !level)) ? (clientConfig.enableSites ? ' - ' + t.site : '') + (clientConfig.enableLevels ? ' - ' + t.level : '') : '')} checked={type.some(s => (!clientConfig.enableSites || s.site === site) && (!clientConfig.enableLevels || s.level === level) && s === t.type)} onChange={() => {
                if (t.site) {
                    onChange({ site: t.site });
                }
                if (t.level) {
                    onChange({ level: t.level });
                }
                onChange({ type: type.includes(t.type) ? type.filter(x => x !== t.type) : [...type, t.type] });
            }}></Checkbox></Col> : null)}
        </Row>)}
    </>
}